import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatRand, STOCK_NOTICE, mediaUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { CalendarClock, Minus, Plus, ShoppingBag, ChevronRight, CheckCircle2 } from "lucide-react";
import { ColourPicker } from "@/components/ColourPicker";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState("");
  const [colour, setColour] = useState(null);
  const [colourError, setColourError] = useState(false);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        setActiveImg(0);
        setSize("");
        setColour(r.data.colours?.length === 1 ? r.data.colours[0] : null);
        setColourError(false);
        setQty(1);
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Product not found.</p>
        <Link to="/shop" className="text-fuchsia-600 font-semibold hover:underline">← Back to shop</Link>
      </div>
    );

  if (!product)
    return (
      <div className="mx-auto max-w-7xl px-6 py-10 grid lg:grid-cols-2 gap-10">
        <div className="aspect-[4/5] rounded-3xl bg-purple-50 animate-pulse" />
        <div className="space-y-4">
          <div className="h-8 w-2/3 bg-purple-50 rounded animate-pulse" />
          <div className="h-6 w-1/3 bg-purple-50 rounded animate-pulse" />
        </div>
      </div>
    );

  const colours = product.colours || [];
  const mainImg = colour?.image || product.images?.[activeImg];

  const handleAdd = () => {
    if (colours.length && !colour) {
      setColourError(true);
      toast.error("Please choose a colour first");
      return;
    }
    if (!size) {
      toast.error("Please select a size first");
      return;
    }
    addItem(product, size, colour?.name, qty, colour?.image);
  };

  const pickColour = (c) => {
    setColour(c);
    setColourError(false);
  };

  return (
    <div data-testid="product-detail-page" className="mx-auto max-w-7xl px-6 py-8">
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6">
        <Link to="/" className="hover:text-fuchsia-600">Home</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <Link to={`/shop/${product.category}`} className="hover:text-fuchsia-600 capitalize">{product.category}</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-purple-900 font-medium truncate">{product.name}</span>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div>
          <div className="rounded-3xl overflow-hidden border border-purple-100 bg-gradient-to-br from-purple-50 to-fuchsia-50 aspect-square flex items-center justify-center p-6">
            <img data-testid="product-main-image" src={mediaUrl(mainImg)} alt={colour ? `${product.name} – ${colour.name}` : product.name} className="max-w-full max-h-full object-contain" />
          </div>
          {colours.length > 0 && (
            <ColourPicker colours={colours} value={colour?.name} onChange={pickColour} error={colourError} />
          )}
          {!colours.length && product.images?.length > 1 && (
            <div className="flex gap-3 mt-4">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`w-20 h-24 rounded-xl overflow-hidden border-2 bg-purple-50 flex items-center justify-center p-1 ${activeImg === i ? "border-fuchsia-500" : "border-transparent"}`}
                >
                  <img src={mediaUrl(img)} alt="" className="max-w-full max-h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 capitalize">{product.type}</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-purple-950 mt-3">{product.name}</h1>
          <p className="text-2xl font-bold brand-gradient-text mt-3">{formatRand(product.price)}</p>
          <p className="text-xs text-muted-foreground">Incl. VAT · Excludes delivery</p>

          <div data-testid="stock-availability-notice" className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium">
            <CalendarClock className="w-4 h-4" /> {STOCK_NOTICE}
          </div>

          <p className="mt-5 text-gray-600 leading-relaxed">{product.description}</p>

          {colours.length > 0 && (
            <p data-testid="selected-colour-label" className="mt-5 text-sm text-gray-700">
              Colour:{" "}
              {colour ? (
                <strong className="text-purple-900">{colour.name}</strong>
              ) : (
                <span className="text-amber-600 font-medium">choose a colour below the picture</span>
              )}
            </p>
          )}

          {/* Sizes */}
          <div className="mt-6">
            <p className="text-sm font-semibold text-gray-800 mb-2">Select size</p>
            <div className="flex flex-wrap gap-2" data-testid="size-selector">
              {product.sizes?.map((s) => (
                <button
                  key={s}
                  data-testid={`product-size-select-${s}`}
                  onClick={() => setSize(s)}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition ${
                    size === s
                      ? "brand-gradient text-white border-transparent"
                      : "bg-white border-purple-200 text-purple-800 hover:border-fuchsia-400"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Qty + add */}
          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border border-purple-200 rounded-full">
              <button className="p-3" onClick={() => setQty((q) => Math.max(1, q - 1))}>
                <Minus className="w-4 h-4" />
              </button>
              <span className="px-3 font-semibold w-8 text-center" data-testid="qty-value">{qty}</span>
              <button className="p-3" onClick={() => setQty((q) => q + 1)}>
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              data-testid="add-to-cart-button"
              onClick={handleAdd}
              className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-full brand-gradient text-white font-semibold hover:opacity-90 transition shadow-lg shadow-fuchsia-200"
            >
              <ShoppingBag className="w-5 h-5" /> Add to Bag
            </button>
          </div>

          <div className="mt-4 flex items-center gap-2 text-sm text-emerald-600 font-medium">
            <CheckCircle2 className="w-4 h-4" /> In stock — reserve yours now
          </div>

          <Accordion type="single" collapsible className="mt-8">
            <AccordionItem value="care">
              <AccordionTrigger className="text-sm font-semibold">Fabric & Care</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground">
                Made from soft, skin-friendly premium fabrics. Machine wash cold, do not tumble dry, iron on low. Colours stay vibrant wash after wash.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="delivery">
              <AccordionTrigger className="text-sm font-semibold">Delivery & Returns</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground">
                Nationwide delivery across South Africa. New season stock ships from 1st October 2026. Easy 14-day returns and size exchanges.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
}
